<template>
    <div class="factor-manager">
        <TabContainer :tabs="tabs" :activeTab.sync="activeTab">
            <template #actions="{ activeTab: currentTab }">
                <div v-if="currentTab === 'factor'" class="factor-header">
                    <div class="tab-controls">
                        <CarbonInput v-model="searchKeyword" variant="search" placeholder="검색" />
                        <CarbonSelect
                            v-model="filterFuelType"
                            :options="fuelTypeOptions"
                            placeholder="전체 연료"
                            :placeholderDisabled="false"
                        />
                        <CarbonSelect
                            v-model="filterDraftStat"
                            :options="draftStats"
                            placeholder="전체 상태"
                            :placeholderDisabled="false"
                        />
                        <CarbonButton
                            icon-only
                            icon="fas fa-plus"
                            title="신규 등록"
                            @click="openFactorModal('create')"
                        />
                    </div>
                </div>
                <div v-else-if="currentTab === 'gwp'" class="factor-header">
                    <div class="tab-controls">
                        <CarbonButton
                            icon-only
                            icon="fas fa-plus"
                            title="신규 프로파일"
                            @click="$refs.gwpProfileRef?.openProfileModal?.('create')"
                        />
                    </div>
                </div>
            </template>
            <template #default="{ activeTab: currentTab }">
                <!-- 배출계수 탭 -->
                <div v-if="currentTab === 'factor'" class="tab-content">
                    <FactorManagement
                        :factors="factors"
                        :loading="loadingFactors"
                        :filter-fuel-type="filterFuelType"
                        :filter-draft-stat="filterDraftStat"
                        :search-keyword="searchKeyword"
                        :current-page.sync="currentFactorPage"
                        :items-per-page="itemsPerPage"
                        @update:itemsPerPage="onFactorPageSizeChange"
                        @view-detail="viewFactorDetail"
                        @view-history="openFactorHistory"
                        @edit="(factor) => openFactorModal('edit', factor)"
                        @delete="confirmDeleteFactor"
                        @update:total-filtered="totalFilteredFactors = $event"
                    />

                    <!-- 배출계수 이력 조회 모달 -->
                    <FactorHistoryModal
                        :visible="showFactorHistoryModal"
                        :factor="selectedFactorForHistory"
                        @update:visible="onFactorHistoryVisibleChange"
                        @value-added="onFactorValueChanged"
                        @value-updated="onFactorValueChanged"
                        @value-deleted="onFactorValueChanged"
                    />

                    <!-- 배출계수 상세보기 모달 -->
                    <b-modal v-model="showFactorDetailModal" title="배출계수 상세 정보" size="lg" centered>
                        <template #modal-header="{ close }">
                            <div class="fd-header">
                                <span class="fd-header-title">배출계수 상세</span>
                                <button type="button" class="fd-header-close" @click="close" aria-label="닫기">
                                    &times;
                                </button>
                            </div>
                        </template>

                        <div v-if="selectedFactor" class="fd-body">
                            <!-- 1. 식별 영역 -->
                            <div class="fd-identity">
                                <div class="fd-identity-left">
                                    <span class="fd-identity-name">
                                        {{ selectedFactor.factorName || selectedFactor.factorCode || "-" }}
                                    </span>
                                    <span
                                        v-if="selectedFactor.factorName && selectedFactor.factorCode"
                                        class="fd-identity-code"
                                    >
                                        {{ selectedFactor.factorCode }}
                                    </span>
                                </div>
                                <span
                                    class="fd-status-badge"
                                    :class="{
                                        'fd-status-badge--active': selectedFactor.draftStat === 'ACTIVE',
                                        'fd-status-badge--inactive': selectedFactor.draftStat === 'INACTIVE',
                                    }"
                                >
                                    {{ getDraftStatLabel(selectedFactor.draftStat) || "-" }}
                                </span>
                            </div>

                            <!-- 2. 컨텍스트 줄 -->
                            <div class="fd-context">
                                <span class="fd-context-item">
                                    <span class="fd-context-label">유형</span>
                                    <span class="fd-context-value">
                                        {{ getFactorTypeLabel(selectedFactor.factorType) || "-" }}
                                    </span>
                                </span>
                                <span class="fd-context-sep">·</span>
                                <span class="fd-context-item">
                                    <span class="fd-context-label">연료</span>
                                    <span class="fd-context-value">
                                        {{ getFuelTypeLabel(selectedFactor.fuelType) || "-" }}
                                    </span>
                                </span>
                                <span class="fd-context-sep">·</span>
                                <span class="fd-context-item">
                                    <span class="fd-context-label">국가</span>
                                    <span class="fd-context-value">{{ selectedFactor.country || "-" }}</span>
                                </span>
                                <span class="fd-context-sep">·</span>
                                <span class="fd-context-item">
                                    <span class="fd-context-label">단위</span>
                                    <span class="fd-context-value">
                                        {{ getUnitLabel(getSelectedFactorDisplayUnit()) || "-" }}
                                    </span>
                                </span>
                                <span class="fd-context-sep">·</span>
                                <span class="fd-context-item">
                                    <span class="fd-context-label">기간</span>
                                    <span class="fd-context-value">
                                        {{
                                            formatFactorPeriod(
                                                selectedFactorActiveValue && selectedFactorActiveValue.startMonth,
                                                selectedFactorActiveValue && selectedFactorActiveValue.endMonth,
                                            )
                                        }}
                                    </span>
                                </span>
                                <span class="fd-context-sep">·</span>
                                <span class="fd-context-item">
                                    <span class="fd-context-label">출처</span>
                                    <span class="fd-context-value">
                                        {{
                                            getFactorSourceLabel(
                                                selectedFactorActiveValue && selectedFactorActiveValue.source,
                                            )
                                        }}
                                    </span>
                                </span>
                            </div>

                            <!-- 3. 배출량 -->
                            <div class="fd-section">
                                <div class="fd-section-label">배출량</div>
                                <div class="fd-gas-grid">
                                    <div
                                        v-for="c in selectedFactorGasComponents"
                                        :key="c.componentIdx || c.componentCode"
                                        class="fd-gas-card"
                                    >
                                        <div class="fd-gas-header">
                                            <span class="fd-gas-code">{{ c.componentCode }}</span>
                                            <span class="fd-gas-name">{{ getGasSpeciesName(c.componentCode) }}</span>
                                        </div>
                                        <div class="fd-gas-value">{{ formatGasValue(c.emissionFactor) }}</div>
                                        <div class="fd-gas-unit">{{ c.emissionUnit || "-" }}</div>
                                    </div>
                                </div>
                            </div>

                            <!-- 4. 발열량 -->
                            <div v-if="selectedFactorActiveValue" class="fd-section">
                                <div class="fd-section-label">발열량</div>
                                <div class="fd-cal-grid">
                                    <div class="fd-cal-row">
                                        <span class="fd-cal-label">순발열량 (NCV)</span>
                                        <span class="fd-cal-value">
                                            {{ formatCalorificValue(selectedFactorActiveValue, "NCV") }}
                                        </span>
                                    </div>
                                    <div class="fd-cal-row">
                                        <span class="fd-cal-label">총발열량 (GCV)</span>
                                        <span class="fd-cal-value">
                                            {{ formatCalorificValue(selectedFactorActiveValue, "GCV") }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- 5. 비고 + 메타 -->
                            <div class="fd-meta">
                                <span
                                    v-if="selectedFactor.description || selectedFactor.remarks"
                                    class="fd-meta-remark"
                                >
                                    {{ selectedFactor.description || selectedFactor.remarks }}
                                </span>
                                <span class="fd-meta-dates">
                                    등록 {{ formatDateTime(selectedFactor.regDate) }} &nbsp;·&nbsp; 수정
                                    {{ formatDateTime(selectedFactor.modDate) }}
                                </span>
                            </div>
                        </div>

                        <template #modal-footer>
                            <CarbonButton variant="secondary" @click="showFactorDetailModal = false">닫기</CarbonButton>
                        </template>
                    </b-modal>

                    <FactorFormModal
                        mode="create"
                        :visible.sync="showCreateFactorModal"
                        :factors="factors"
                        @success="onFactorFormSuccess"
                    />
                    <FactorFormModal
                        mode="edit"
                        :visible.sync="showEditFactorModal"
                        :factor-to-edit="editFactorTarget"
                        @success="onFactorFormSuccess"
                    />
                </div>
                <!-- GWP 프로파일 탭 -->
                <div v-else-if="currentTab === 'gwp'" class="tab-content">
                    <GwpProfileMain ref="gwpProfileRef" />
                </div>
            </template>
        </TabContainer>
    </div>
</template>

<script>
import TabContainer from "../../common/TabContainer.vue";
import FactorManagement from "../views/FactorManagement.vue";
import GwpProfileMain from "../../gwp/GwpProfileMain.vue";
import carbonApi from "@/submodules/carbon-plugin/api";
import CarbonCodes from "../../../codes";
import FactorHistoryModal from "../modals/FactorHistoryModal.vue";
import FactorFormModal from "../modals/FactorFormModal.vue";
import CarbonButton from "../../common/CarbonButton.vue";
import CarbonInput from "../../common/CarbonInput.vue";
import CarbonSelect from "../../common/CarbonSelect.vue";
import {
    deriveCurrentValueAndUnit,
    deriveAggrGasCode,
    getGasFactorsFromComponents,
    getCalorificFromComponents,
} from "@/submodules/carbon-plugin/utils/factorComponentHelper";

export default {
    name: "FactorMain",
    components: {
        TabContainer,
        FactorManagement,
        FactorHistoryModal,
        FactorFormModal,
        GwpProfileMain,
        CarbonButton,
        CarbonInput,
        CarbonSelect,
    },
    props: {
        loginUser: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            activeTab: "factor",
            tabs: [
                { label: "배출계수", value: "factor" },
                { label: "GWP 프로파일", value: "gwp" },
            ],
            // 배출계수 관련
            factors: [],
            loadingFactors: false,
            filterFuelType: "",
            filterDraftStat: "",
            searchKeyword: "",
            currentFactorPage: 1,
            itemsPerPage: 10,
            totalFilteredFactors: 0,

            // 모달 관련
            showCreateFactorModal: false,
            showEditFactorModal: false,
            editFactorTarget: null,

            showFactorDetailModal: false,
            selectedFactor: null,

            showFactorHistoryModal: false,
            selectedFactorForHistory: null,
        };
    },
    computed: {
        // 공통 코드
        gwpVersions() {
            const list = CarbonCodes.gwpVersion.getList();
            if (list.length > 0) return list;
            return [
                { value: "AR4", text: "AR4" },
                { value: "AR5", text: "AR5" },
                { value: "AR6", text: "AR6" },
            ];
        },
        draftStats() {
            return CarbonCodes.draftStat.getList();
        },
        fuelTypeOptions() {
            return CarbonCodes.fuelType.getList();
        },
        factorTypeOptions() {
            const list = CarbonCodes.factorType?.getList?.() ?? [];
            if (Array.isArray(list) && list.length > 0) return list;
            return [
                { value: "FUEL", text: "FUEL" },
                { value: "ACTIVITY", text: "ACTIVITY" },
            ];
        },
        /** 열량계수 단위 (CALORIFIC_UNIT 공통코드, 문서: 배출계수 체계화) */
        calorificUnitOptions() {
            const list = CarbonCodes.calorificUnit?.getList?.() ?? [];
            return Array.isArray(list) ? list : [];
        },
        regionOptions() {
            const list = [
                { value: "KR", text: "한국" },
                { value: "EU", text: "유럽" },
            ];
            return list;
        },
        /** ACTIVITY_UNIT 공통코드 (260308) - 배출계수 단위 */
        activityUnitOptions() {
            return CarbonCodes.activityUnit.getList();
        },
        /** FACTOR_SOURCE 공통코드 (260311) - 출처/방법론 */
        factorSourceOptions() {
            const list = CarbonCodes.factorSource?.getList?.() ?? [];
            return Array.isArray(list) ? list : [];
        },

        // 필터용 연료 유형 목록 (unique)
        fuelTypes() {
            const types = [...new Set(this.factors.map((f) => f.fuelType).filter(Boolean))];
            return types.sort();
        },
        /** 배출계수 필터 결과 총 페이지 수 */
        totalFactorPages() {
            if (this.totalFilteredFactors <= 0 || this.itemsPerPage <= 0) return 1;
            return Math.ceil(this.totalFilteredFactors / this.itemsPerPage);
        },

        /** 배출계수 상세 모달에서 "현재 유효값"으로 쓸 factorValues 1개 */
        selectedFactorActiveValue() {
            const values = this.selectedFactor?.factorValues;
            if (!Array.isArray(values) || values.length === 0) return null;
            return values.find((v) => v && (v.endMonth == null || v.endMonth === "")) || values[0];
        },

        /** 배출계수 상세 모달: NCV/GCV 제외 가스·종별 component 전체 */
        selectedFactorGasComponents() {
            const fv = this.selectedFactorActiveValue;
            const comps = fv?.components;
            if (!Array.isArray(comps) || comps.length === 0) return [];

            const basketOrder = { CO2: 0, CH4: 1, N2O: 2, HFCs: 3, PFCs: 4, SF6: 5 };

            return comps
                .map((c) => ({
                    componentIdx: c?.componentIdx ?? null,
                    componentCode: String(c?.componentCode || "").trim(),
                    emissionFactor: c?.emissionFactor,
                    emissionUnit: String(c?.emissionUnit || "").trim(),
                }))
                .filter((c) => c.componentCode && c.componentCode !== "NCV" && c.componentCode !== "GCV")
                .sort((a, b) => {
                    const ba = deriveAggrGasCode(a.componentCode);
                    const bb = deriveAggrGasCode(b.componentCode);
                    const oa = ba != null && basketOrder[ba] !== undefined ? basketOrder[ba] : 50;
                    const ob = bb != null && basketOrder[bb] !== undefined ? basketOrder[bb] : 50;
                    if (oa !== ob) return oa - ob;
                    const ai = Number(a.componentIdx || 0);
                    const bi = Number(b.componentIdx || 0);
                    if (ai !== bi) return ai - bi;
                    return String(a.componentCode).localeCompare(String(b.componentCode));
                });
        },
    },
    watch: {
        searchKeyword() {
            this.currentFactorPage = 1;
        },
        filterFuelType() {
            this.currentFactorPage = 1;
        },
        filterDraftStat() {
            this.currentFactorPage = 1;
        },
    },
    mounted() {
        this.loadFactors();
    },

    methods: {
        onFactorPageSizeChange(size) {
            this.itemsPerPage = size;
            this.currentFactorPage = 1;
        },
        _currentYYYYMM() {
            const now = new Date();
            return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}`;
        },
        getFactorTypeLabel(value) {
            return CarbonCodes.factorType.getLabel(value);
        },
        getFactorSourceLabel(value) {
            if (!value) return "-";
            return CarbonCodes.factorSource.getLabel(value);
        },
        getFuelTypeLabel(value) {
            return CarbonCodes.fuelType.getLabel(value);
        },
        getUnitLabel(value) {
            return value;
            // return CarbonCodes.activityUnit.getLabel(value) || value || "-";
        },
        /** GHG_SPECIES 공통코드의 name(text) — 가스 입력란 서브타이틀용 */
        getGasSpeciesName(code) {
            return CarbonCodes.gasCode?.getLabel?.(code) ?? code ?? "";
        },
        // 배출계수 API
        async loadFactors() {
            this.loadingFactors = true;
            try {
                const response = await carbonApi.emissionFactor.getFactors();
                const apiData = response.data.data || response.data;

                if (apiData && apiData.factors) {
                    this.factors = apiData.factors.map((factor) => {
                        const description = factor.description ?? factor.remarks ?? "";
                        // 문서: currentValue/unit은 components 기반. 목록에 components 있으면 유도.
                        const { currentValue: derivedValue, displayUnit: derivedUnit } =
                            deriveCurrentValueAndUnit(factor);
                        const currentValue = factor.currentValue ?? factor.factor ?? derivedValue;
                        return {
                            ...factor,
                            description,
                            remarks: factor.remarks ?? description,
                            factor: currentValue,
                            currentValue,
                            unit: factor.unit ?? derivedUnit,
                            draftStat: factor.draftStat || (factor.isActive ? "ACTIVE" : "INACTIVE"),
                            regDt: this.parseDateNumber(factor.regDate) || this.parseDateNumber(factor.regDt),
                            updDt: this.parseDateNumber(factor.modDate) || this.parseDateNumber(factor.updDt),
                        };
                    });
                } else {
                    console.warn("[FactorManager] 배출계수 응답 형식이 예상과 다릅니다:", response.data);
                    this.factors = [];
                }
            } catch (error) {
                console.error("[FactorManager] 배출계수 로딩 실패:", error);
                this.$bvToast.toast("배출계수를 불러오는데 실패했습니다.", {
                    title: "오류",
                    variant: "danger",
                    solid: true,
                });
                this.factors = [];
            } finally {
                this.loadingFactors = false;
            }
        },

        openFactorModal(mode, factor = null) {
            if (mode === "create") {
                this.showCreateFactorModal = true;
            } else if (mode === "edit" && factor) {
                this.editFactorTarget = factor;
                this.showEditFactorModal = true;
            }
        },

        onFactorFormSuccess(payload) {
            this.loadFactors();
            if (payload && payload.created) {
                this.currentFactorPage = 1;
            }
        },

        async viewFactorDetail(factor) {
            if (!factor?.factorIdx) return;
            try {
                const res = await carbonApi.emissionFactor.getFactorById(factor.factorIdx);
                const data = res.data?.data || res.data;
                this.selectedFactor = data?.factor || factor;
                this.showFactorDetailModal = true;
            } catch (err) {
                console.error("[FactorMain] 배출계수 상세 조회 실패:", err);
                this.$bvToast.toast("배출계수 상세 정보를 불러오는데 실패했습니다.", {
                    title: "오류",
                    variant: "danger",
                    solid: true,
                });
            }
        },

        async confirmDeleteFactor(factor) {
            const result = await this.alertConfirmWarning("배출계수를 삭제하시겠습니까?");
            if (!result.value) return false;

            await this.deleteFactor(factor);

            return true;
        },

        async deleteFactor(factor) {
            try {
                // Carbon Plugin API 호출
                let result = await carbonApi.emissionFactor.deleteFactor(factor.factorIdx);

                this.$bvToast.toast(result.data.message, {
                    title: "성공",
                    variant: "success",
                    solid: true,
                });

                // 목록 새로고침
                await this.loadFactors();
            } catch (error) {
                console.error("[FactorManager] 배출계수 삭제 실패:", error);
                const errorMessage = error.response?.data?.message || error.message || "배출계수 삭제에 실패했습니다.";
                this.$bvToast.toast(errorMessage, {
                    title: "오류",
                    variant: "danger",
                    solid: true,
                });
            }
        },

        // 유틸리티
        parseDateNumber(dateNum) {
            // 숫자 형식 날짜(20251014151056)를 ISO 형식으로 변환
            if (!dateNum) return null;

            const dateStr = dateNum.toString();
            if (dateStr.length < 14) return null;

            const year = dateStr.substring(0, 4);
            const month = dateStr.substring(4, 6);
            const day = dateStr.substring(6, 8);
            const hour = dateStr.substring(8, 10);
            const minute = dateStr.substring(10, 12);
            const second = dateStr.substring(12, 14);

            return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
        },

        formatDate(dateString) {
            if (!dateString) return "-";
            try {
                return new Date(dateString).toLocaleDateString("ko-KR");
            } catch (e) {
                return dateString;
            }
        },

        formatDateShort(dateString) {
            if (!dateString) return "-";
            try {
                // YYYY-MM-DD 형식으로 표시
                return dateString.split("T")[0];
            } catch (e) {
                return dateString;
            }
        },

        formatDateTime(dateString) {
            if (!dateString) return "-";
            try {
                return new Date(dateString).toLocaleString("ko-KR");
            } catch (e) {
                return dateString;
            }
        },

        getTruncatedText(text, maxLength) {
            if (!text) return "";
            if (text.length <= maxLength) return text;
            return text.substring(0, maxLength) + "...";
        },

        // 배출계수 이력 조회 열기 (재오픈 버그 방지: null로 리셋 후 nextTick에서 설정)
        openFactorHistory(factor) {
            this.selectedFactorForHistory = null;
            this.$nextTick(() => {
                this.selectedFactorForHistory = factor;
                this.showFactorHistoryModal = true;
            });
        },

        onFactorHistoryVisibleChange(val) {
            this.showFactorHistoryModal = val;
            if (!val) this.selectedFactorForHistory = null;
        },

        onFactorValueChanged() {
            this.loadFactors();
            if (this.selectedFactorForHistory && this.factors.length > 0) {
                const updated = this.factors.find((f) => f.factorIdx === this.selectedFactorForHistory.factorIdx);
                if (updated) this.selectedFactorForHistory = updated;
            }
        },

        getDraftStatLabel(draftStat) {
            return CarbonCodes.draftStat.getLabel(draftStat);
        },

        formatFactorPeriod(startMonth, endMonth) {
            if (!startMonth) return "-";
            const start = `${String(startMonth).slice(0, 4)}-${String(startMonth).slice(4, 6)}`;
            const end = endMonth ? `${String(endMonth).slice(0, 4)}-${String(endMonth).slice(4, 6)}` : "현재";
            return `${start} ~ ${end}`;
        },

        formatGasValue(val) {
            if (val == null || val === undefined) return "-";
            const n = Number(val);
            return n === 0 ? "0" : n;
        },

        /**
         * 상세 모달: 열량계수(NCV/GCV) 값 + 단위를 함께 표시
         * - 값만 있으면 값만, 단위가 있으면 "값 단위" 형태로 표시
         */
        formatCalorificValue(fv, kind) {
            const { ncv, ncvUnit, gcv, gcvUnit } = getCalorificFromComponents(fv);
            const value = kind === "NCV" ? ncv : gcv;
            const unit = kind === "NCV" ? ncvUnit : gcvUnit;

            if (value == null || value === undefined) return "-";

            const n = Number(value);
            const valueText = n === 0 ? "0" : n;

            if (!unit) return valueText;
            return `${valueText} ${unit}`;
        },

        /** 상세 모달: value의 components 또는 legacy 플랫 필드에서 가스 맵 */
        getGasForValue(fv) {
            return getGasFactorsFromComponents(fv);
        },

        /** 상세 모달: selectedFactor의 표시 단위 (components 기반 또는 factor.unit) */
        getSelectedFactorDisplayUnit() {
            if (!this.selectedFactor) return "-";
            const { displayUnit } = deriveCurrentValueAndUnit(this.selectedFactor);
            if (displayUnit && displayUnit !== "-") return displayUnit;
            return this.selectedFactor.unit ?? "-";
        },
    },
};
</script>

<style scoped>
/* .row {
    margin-bottom: 10px;
} */
.row {
    margin: 6px 0px;
}
.factor-manager {
    overflow-y: auto;
    width: 100%;
    box-sizing: border-box;
    padding: 0px 16px;
}

.factor-header {
    /* padding: 12px; */
    background: #f8fafc;
}

.tab-controls {
    display: flex !important;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    justify-content: flex-end;
    width: 100%;
    min-width: 0;
    visibility: visible !important;
    opacity: 1 !important;
}

/* 공통 높이: 32px (공통 스타일 보강) */
.search-input-compact,
.filter-select,
.icon-btn-compact {
    height: 32px;
    box-sizing: border-box;
}

/* 탭 컨텐츠 */

.content-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 12px;
}

.content-title {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: #2c3e50;
}

.item-count {
    background: #e8f5ee;
    color: #52b788;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 500;
}

.header-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.filter-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.filter-select {
    padding: 0 10px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 12px;
    background: #ffffff;
    cursor: pointer;
    transition: all 0.3s;
    min-width: 120px;
}

.filter-select option {
    color: #495057;
}

.filter-select:focus {
    outline: none;
    border-color: #52b788;
    box-shadow: 0 0 0 2px rgba(82, 183, 136, 0.1);
}

.search-input {
    padding: 8px 12px;
    border: 1px solid #ced4da;
    border-radius: 6px;
    font-size: 14px;
    min-width: 200px;
    transition: all 0.3s;
}

.search-input:focus {
    outline: none;
    border-color: #52b788;
    box-shadow: 0 0 0 2px rgba(82, 183, 136, 0.1);
}

.primary-btn {
    padding: 10px 20px;
    background: #52b788;
    color: #ffffff;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 6px;
}

.primary-btn:hover {
    background: #2d8659;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(82, 183, 136, 0.4);
}

.primary-btn:focus {
    outline: none;
}

.primary-btn i {
    font-size: 18px;
}

/* 테이블 */
/* table-compact 룩/테이블 룩은 전역 _carbon-tables.scss 적용 */

.table-wrapper {
    overflow-x: auto;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    margin-bottom: 16px;
    max-height: 460px;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
    background: #ffffff;
}

.data-table thead {
    background: #f8fafc;
    border-bottom: 2px solid #e2e8f0;
}

.data-table th {
    padding: 12px 10px;
    text-align: left;
    font-size: 13px;
    font-weight: 600;
    color: #495057;
    white-space: nowrap;
}

.data-table td {
    padding: 12px 10px;
    font-size: 12px;
    color: #495057;
    border-bottom: 1px solid #e9ecef;
    vertical-align: middle;
}

.data-table tbody tr:hover {
    background: #f0f9f4;
}

.data-table tbody tr.inactive-row {
    opacity: 0.6;
}

.data-table tbody tr:last-child td {
    border-bottom: none;
}

.text-center {
    text-align: center;
    padding: 40px 20px !important;
}

.empty-state {
    text-align: center;
    color: #6c757d;
}

.empty-state i {
    font-size: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
}

.empty-state p {
    margin: 0;
    font-size: 16px;
}

.text-muted {
    color: #6c757d;
}

.status-indicator {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    color: #adb5bd;
}

.status-indicator.active {
    color: #495057;
}

.status-indicator i {
    font-size: 16px;
}

.energy-type-tag {
    display: inline-block;
    font-size: 13px;
    font-weight: 500;
}

.factor-value {
    font-weight: 600;
}

.unit-badge {
    display: inline-block;
    font-size: 12px;
}

.methodology-tag {
    display: inline-block;
    font-size: 13px;
    color: #495057;
}

.date-cell {
    font-size: 12px;
    color: #6c757d;
}

.action-buttons {
    display: flex;
    gap: 6px;
}

.icon-btn {
    padding: 4px 8px;
    background: transparent;
    border: 1px solid #ced4da;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.icon-btn:hover {
    background: #f0f9f4;
    border-color: #52b788;
}

.icon-btn-mini {
    padding: 4px 8px;
    border: 1px solid #cbd5e1;
    border-radius: 3px;
    background: transparent;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 12px;
}

.icon-btn-mini:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
}

.icon-btn-mini.danger:hover {
    background: #fef2f2;
}

.icon-btn.activate {
    background: #52b788;
    border-color: #52b788;
    color: #ffffff;
}

.icon-btn.activate:hover {
    background: #40916c;
    border-color: #40916c;
}

.icon-btn.activate:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.icon-btn.activate.current {
    background: #2d6a4f;
    border-color: #2d6a4f;
    opacity: 0.85;
}

.icon-btn.activate.current:hover {
    background: #2d6a4f;
    border-color: #2d6a4f;
    color: #ffffff;
}

.icon-btn.activate.current i {
    color: #ffffff;
}

.icon-btn:focus {
    outline: none;
}

.icon-btn.delete:hover {
    background: #dc3545;
    border-color: #dc3545;
    color: #ffffff;
}

/* 페이지네이션 */
.pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    margin-top: 20px;
}

.page-btn {
    padding: 4px 12px;
    background: white;
    border: 1px solid #ced4da;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.page-btn:hover:not(:disabled) {
    background: #f0f9f4;
    border-color: #52b788;
    color: #52b788;
}

.page-btn:focus {
    outline: none;
}

.page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-info {
    padding: 8px 16px;
    font-size: 14px;
    color: #495057;
    font-weight: 500;
}

/* 상세보기 모달 */
.detail-view {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.detail-row {
    display: grid;
    grid-template-columns: 120px 1fr;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid #e9ecef;
}

.detail-row.full-width {
    grid-template-columns: 1fr;
}

.detail-row:last-child {
    border-bottom: none;
}

.detail-label {
    font-weight: 600;
    color: #495057;
    font-size: 14px;
}

.detail-value {
    color: #6c757d;
    font-size: 14px;
}

.json-display {
    margin-top: 8px;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 6px;
    font-size: 12px;
    color: #495057;
    overflow-x: auto;
}

/* 폼 스타일 */
.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #495057;
}

.form-group .required {
    color: #dc3545;
}

.form-control {
    width: 100%;
    border: 1px solid #ced4da;
    border-radius: 6px;
    font-size: 14px;
    color: #495057;
    transition: border-color 0.2s;
}

.form-control:focus {
    outline: none;
    border-color: #52b788;
    box-shadow: 0 0 0 3px rgba(82, 183, 136, 0.1);
}

.form-control::placeholder {
    color: #adb5bd;
}

.form-text {
    display: block;
    font-size: 12px;
}

.custom-control-label {
    font-weight: 400;
}

/* 애니메이션 */
@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.fa-spin {
    animation: spin 1s linear infinite;
}

/* 반응형 */
@media (max-width: 768px) {
    .content-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .header-right {
        width: 100%;
        flex-direction: column;
    }

    .filter-controls {
        width: 100%;
    }

    .filter-select,
    .search-input {
        width: 100%;
    }

    .primary-btn {
        width: 100%;
        justify-content: center;
    }

    .detail-row {
        grid-template-columns: 1fr;
        gap: 8px;
    }
}

/* 복사 버튼 */
.icon-btn.copy {
    background: #0dcaf0;
    color: white;
}

.icon-btn.copy:hover {
    background: #0ab3d5;
}

/* 작은 버튼 */
.icon-btn.small {
    padding: 4px 8px;
    font-size: 12px;
}

.icon-btn.small.primary {
    background: #52b788;
    color: white;
}

.icon-btn.small.primary:hover {
    background: #40916c;
}

.comparison-table td.value {
    font-weight: 500;
}

.comparison-table td.difference-percent.positive {
    color: #dc3545;
    font-weight: bold;
}

.comparison-table td.difference-percent.negative {
    color: #28a745;
    font-weight: bold;
}

/* Empty State Large */
.empty-state-large {
    text-align: center;
    padding: 60px 20px;
    color: #6c757d;
}

.empty-state-large h3 {
    margin: 16px 0 8px;
    color: #495057;
}

.empty-state-large p {
    margin: 0;
    font-size: 14px;
}

/* Help Text */
.help-text {
    font-size: 13px;
    color: #6c757d;
    margin: 4px 0 0;
}

/* ============================================
   Notion 와이어프레임 기준 스타일 (v2.4.1+)
   ============================================ */

/* 연료 유형 태그 */
.fuel-type-tag {
    display: inline-block;
    font-size: 12px;
    font-weight: 500;
}

/* 상태 배지 */
.status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
}

/* 버튼 스타일 개선 */
.secondary-btn {
    padding: 8px 16px;
    background: #6c757d;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.secondary-btn:hover {
    background: #5a6268;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.secondary-btn i {
    font-size: 14px;
}

/* factors-table 특화 스타일 */
.factors-table .factor-value {
    font-weight: 600;
    color: #495057;
}

/* 배출계수 등록 폼 스타일 (Notion 화면 2) - 시작월 DatePicker 메인색을 폼 테마(#52b788)에 맞춤 */
.start-month-picker-wrapper :deep(.mx-datepicker),
.end-month-picker-wrapper :deep(.mx-datepicker) {
    width: 100%;
}
.start-month-picker-wrapper :deep(.mx-input:hover),
.start-month-picker-wrapper :deep(.mx-input:focus),
.end-month-picker-wrapper :deep(.mx-input:hover),
.end-month-picker-wrapper :deep(.mx-input:focus) {
    border-color: #52b788;
}
.start-month-picker-wrapper :deep(.mx-btn:hover),
.end-month-picker-wrapper :deep(.mx-btn:hover) {
    border-color: #52b788 !important;
    color: #52b788 !important;
    background-color: transparent !important;
}

/* 셀 호버/범위/활성 상태를 폼 테마(#52b788) 톤으로 통일 */
.start-month-picker-wrapper :deep(.mx-calendar-content .cell:hover),
.end-month-picker-wrapper :deep(.mx-calendar-content .cell:hover) {
    background-color: rgba(82, 183, 136, 0.16) !important;
    color: #2f855a !important;
}
.start-month-picker-wrapper :deep(.mx-calendar-content .cell.in-range),
.start-month-picker-wrapper :deep(.mx-calendar-content .cell.hover-in-range),
.end-month-picker-wrapper :deep(.mx-calendar-content .cell.in-range),
.end-month-picker-wrapper :deep(.mx-calendar-content .cell.hover-in-range) {
    background-color: rgba(82, 183, 136, 0.14) !important;
    color: #475569 !important;
}
.start-month-picker-wrapper :deep(.mx-calendar-content .cell.active),
.end-month-picker-wrapper :deep(.mx-calendar-content .cell.active) {
    color: #fff !important;
    background-color: #52b788 !important;
}
.start-month-picker-wrapper :deep(.mx-table-date .today),
.end-month-picker-wrapper :deep(.mx-table-date .today) {
    color: #52b788;
}
.start-month-picker-wrapper :deep(.mx-time-column .mx-time-item.active),
.start-month-picker-wrapper :deep(.mx-time-option.active),
.end-month-picker-wrapper :deep(.mx-time-column .mx-time-item.active),
.end-month-picker-wrapper :deep(.mx-time-option.active) {
    color: #52b788;
}

/* 배출계수 코드: 상태와 같은 행(col-6)에 배치해 세로 영역 제거 */
.factorcode-inline {
    margin-bottom: 0;
}
.factorcode-inline .factorcode-preview {
    display: flex;
    align-items: center;
    gap: 6px;
}
.factorcode-inline .code-input {
    flex: 1;
    min-width: 0;
    font-size: 12px;
    font-weight: 600;
    color: #2d8659;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    padding: 6px 10px;
}
.factorcode-preview .check-badge {
    flex-shrink: 0;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}

.factorcode-preview .check-badge.success {
    background: #d4edda;
    color: #28a745;
}

.factorcode-preview .check-badge.error {
    background: #f8d7da;
    color: #dc3545;
}

.factorcode-preview .check-badge.checking {
    background: #fff3cd;
    color: #ffc107;
}

.factorcode-preview .check-badge-hint {
    font-size: 10px;
    opacity: 0.85;
    font-weight: 500;
}

/* 라디오 그룹 스타일 */
.radio-group {
    display: flex;
    gap: 24px;
    margin: 12px 0;
}

.radio-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 14px;
}

.radio-label input[type="radio"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
}

.radio-label .radio-text {
    font-weight: 500;
}

/* section-divider 스타일 */
.section-divider {
    font-size: 14px;
    font-weight: 600;
    color: #495057;
    margin: 24px 0 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e2e8f0;
}

.modal-detail-content {
    padding: 10px 0;
}

.modal-detail-content-compact {
    padding: 0;
}

/* 가스 배출계수 상세 섹션 */
.factor-values-section {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
}

.factor-values-section .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #495057;
    margin-bottom: 12px;
}

.factor-values-table-wrapper {
    overflow-x: auto;
    max-height: 280px;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
}

.factor-values-detail-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
}

.factor-values-detail-table thead {
    background: #f8fafc;
    position: sticky;
    top: 0;
    z-index: 1;
}

.factor-values-detail-table th {
    padding: 8px 10px;
    text-align: left;
    font-weight: 600;
    color: #495057;
    border-bottom: 2px solid #e2e8f0;
    white-space: nowrap;
}

.factor-values-detail-table td {
    padding: 8px 10px;
    border-bottom: 1px solid #f1f5f9;
}

.factor-values-detail-table th.calorific-header {
    border-left: 1px solid #e2e8f0;
    text-align: center;
}

.factor-values-detail-table td.calorific-cell {
    border-left: 1px solid #e2e8f0;
    background: #f9fafb;
    color: #065f46;
    white-space: nowrap;
    font-weight: 500;
    text-align: center;
}

.factor-values-detail-table .current-value-row {
    background: #f0f9f4;
}

.modal-info-section {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    transition: all 0.2s ease;
}

.modal-info-section:hover {
    border-color: #52b788;
    box-shadow: 0 2px 8px rgba(82, 183, 136, 0.1);
}

.modal-info-section .section-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 7px;
    padding-bottom: 12px;
    border-bottom: 2px solid #e2e8f0;
}

.modal-info-section .section-header i {
    color: #52b788;
    font-size: 16px;
}

.modal-info-section .section-header h6 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #2d8659;
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.info-item .info-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #6c757d;
    letter-spacing: 0.3px;
}

.info-item .info-label i {
    color: #52b788;
    font-size: 12px;
}

.info-item .info-value {
    font-size: 15px;
    font-weight: 500;
    color: #2c3e50;
    padding: 8px 12px;
    background: white;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
}

.info-item .info-value.version-code-display {
    font-weight: 700;
    color: #2d8659;
    background: #e8f5ee;
    border-color: #52b788;
}

.release-notes-content {
    padding: 16px;
    background: white;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    font-size: 14px;
    line-height: 1.6;
    color: #495057;
    white-space: pre-wrap;
}

.json-display-wrapper {
    background: #1e293b;
    border-radius: 6px;
    padding: 16px;
    overflow-x: auto;
}

.json-display {
    margin: 0;
    font-size: 13px;
    color: #e2e8f0;
    line-height: 1.6;
    white-space: pre;
}

.form-section .section-header {
    margin-bottom: 11px;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 10px;
}
.form-section .section-header h6 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #111827;
    letter-spacing: -0.01em;
}
.section-pill {
    width: 3px;
    height: 16px;
    border-radius: 2px;
    background: #059669;
    flex-shrink: 0;
}
.section-pill-gray {
    background: #d1d5db;
}
.form-section .section-header-pill.section-header-green h6,
.form-section .section-header-pill.section-header-gray h6,
.form-section .section-header-green h6 {
    color: #065f46;
    font-weight: 600;
}
.form-section .section-header-gray h6 {
    color: #6b7280;
}

.form-section .form-group {
    margin-bottom: 16px;
}

.form-section .form-group:last-child {
    margin-bottom: 5px;
}

.form-section .form-group label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
    letter-spacing: -0.01em;
}

.form-section .form-group .required {
    color: #dc3545;
    margin-left: 2px;
}

.form-section .form-control {
    width: 100%;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    color: #111827;
    transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;
    background: #fff;
}

.form-section .form-control:hover {
    border-color: #6ee7b7;
}

.form-section .form-control:focus {
    outline: none;
    border-color: #059669;
    box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.1);
}

.form-section .json-textarea {
    font-size: 13px;
    background: #1e293b;
    color: #e2e8f0;
    border-color: #334155;
}

.form-section .json-textarea:focus {
    border-color: #52b788;
    box-shadow: 0 0 0 3px rgba(82, 183, 136, 0.1);
}

.form-section .form-text {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
    font-size: 12px;
    color: #6c757d;
}

.form-section .form-text i {
    font-size: 11px;
}

/* 가스 배출계수 행 컴팩트 간격 */
.gas-factors-row .form-group {
    margin-bottom: 12px;
}
/* gas-factors-row-2x3: 2x3(2열 x 3행) 레이아웃을 grid로 고정 */
.gas-factors-row-2x3 {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;

    margin: 0px 15px;
}
.gas-factors-row-2x3 .form-group {
    margin-bottom: 0; /* gap로만 간격 제어 */
}
.gas-factors-row-2x3 > [class*="col-"] {
    padding-left: 0;
    padding-right: 0;
}
/* grid 셀 폭에 맞춰 col 폭/최대폭을 정리 */
.gas-factors-row-2x3 > .col-md-4,
.gas-factors-row-2x3 > .col-6 {
    width: 100%;
    max-width: 100%;
    flex: none;
}

/* 계수 설정: 가스별 항목(라벨 + 종류명 가로 배치) */
.gas-factor-item-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
}
.gas-factor-item-header label {
    margin-bottom: 0;
}
.gas-factor-item .gas-species-name {
    display: inline;
    font-size: 12px;
    color: #94a3b8;
    margin-bottom: 6px;
    font-weight: 400;
}

.form-section-hint {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 10px 0;
    font-size: 12px;
    color: #64748b;
}
.form-section-hint i {
    flex-shrink: 0;
}

/* 모달 푸터: 전체 너비 사용 (등록 모달은 왼쪽 힌트 + 오른쪽 버튼) */
:deep(.modal-footer) {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    padding: 16px 24px;
    border-top: 1px solid #e2e8f0;
    background: #fff;
}
:deep(.modal-footer > *) {
    flex: 1 1 100%;
    max-width: 100%;
}

:deep(.modal-footer .btn) {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    border-radius: 6px;
    transition: all 0.2s ease;
}

:deep(.modal-footer .btn-secondary) {
    background: #f8fafc;
    color: #475569;
    border: 1px solid #e2e8f0;
    font-weight: 600;
}

:deep(.modal-footer .btn-secondary:hover) {
    background: #f1f5f9;
    border-color: #cbd5e1;
    transform: translateY(-1px);
}

:deep(.modal-footer .btn-primary) {
    background: #059669; /* emerald-600 */
    color: white;
    border: none;
    box-shadow: 0 4px 14px rgba(5, 150, 105, 0.25);
}

:deep(.modal-footer .btn-primary:hover) {
    background: #047857; /* emerald-700 */
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(5, 150, 105, 0.35);
}

:deep(.modal-footer .btn i) {
    font-size: 14px;
}

/* 반응형 */
@media (max-width: 768px) {
    .info-grid {
        grid-template-columns: 1fr;
    }
}

.factor-highlight {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 700;
    color: #2d8659;
    padding: 6px 12px;
    font-size: 16px;
}

.modal-detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0;
    gap: 12px;
}

.modal-detail-header .modal-title {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #2d8659;
    margin: 0;
    flex: 1;
}

.modal-detail-header .modal-title i {
    color: #52b788;
    font-size: 20px;
}

.modal-detail-header .close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    color: #6c757d;
    font-size: 22px;
    border: none;
    background: transparent;
    transition: color 0.2s ease;
}

.modal-detail-header .close:hover {
    color: #2d8659;
}

.modal-detail-header .close span {
    line-height: 1;
}

/* ── 배출계수 상세 정보 모달 (fd-* prefix) ─────────────────── */

/* 헤더 */
.fd-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}
.fd-header-title {
    font-size: 14px;
    font-weight: 700;
    color: #334155;
    letter-spacing: 0.01em;
}
.fd-header-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    background: transparent;
    color: #94a3b8;
    font-size: 20px;
    line-height: 1;
    border-radius: 6px;
    cursor: pointer;
    transition:
        background 0.12s,
        color 0.12s;
}
.fd-header-close:hover {
    background: #f1f5f9;
    color: #475569;
}

/* 본문 */
.fd-body {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 0;
}

/* 1. 식별 영역 */
.fd-identity {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 20px 12px;
    border-bottom: 1px solid #f1f5f9;
}
.fd-identity-left {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
}
.fd-identity-name {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
    word-break: break-word;
}
.fd-identity-code {
    font-size: 11px;
    font-weight: 500;
    color: #94a3b8;
    letter-spacing: 0.02em;
}

/* 상태 뱃지 */
.fd-status-badge {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    padding: 3px 9px;
    border-radius: 9999px;
    font-size: 11px;
    font-weight: 600;
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
    margin-top: 2px;
}
.fd-status-badge--active {
    background: #d1fae5;
    color: #065f46;
    border-color: #a7f3d0;
}
.fd-status-badge--inactive {
    background: #f1f5f9;
    color: #64748b;
    border-color: #e2e8f0;
}

/* 2. 컨텍스트 줄 */
.fd-context {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 0;
    padding: 8px 20px 10px;
    border-bottom: 1px solid #f1f5f9;
}
.fd-context-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.fd-context-label {
    font-size: 11px;
    color: #94a3b8;
    font-weight: 500;
}
.fd-context-value {
    font-size: 12px;
    color: #475569;
    font-weight: 600;
}
.fd-context-sep {
    margin: 0 8px;
    color: #cbd5e1;
    font-size: 13px;
}

/* 3 & 4. 섹션 공통 */
.fd-section {
    padding: 14px 20px;
    border-bottom: 1px solid #f1f5f9;
}
.fd-section-label {
    font-size: 10px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 10px;
}

/* 3. 배출량 가스 그리드 */
.fd-gas-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
}
.fd-gas-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    transition:
        border-color 0.15s,
        box-shadow 0.15s;
}
.fd-gas-card:hover {
    border-color: #a7f3d0;
    box-shadow: 0 2px 8px rgba(82, 183, 136, 0.08);
}
.fd-gas-header {
    display: flex;
    align-items: baseline;
    gap: 5px;
}
.fd-gas-code {
    font-size: 12px;
    font-weight: 700;
    color: #334155;
}
.fd-gas-name {
    font-size: 10px;
    font-weight: 500;
    color: #94a3b8;
}
.fd-gas-value {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.1;
}
.fd-gas-unit {
    font-size: 12px;
    font-weight: 500;
    color: #52b788;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* 4. 발열량 */
.fd-cal-grid {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.fd-cal-row {
    display: grid;
    grid-template-columns: 110px 1fr;
    align-items: center;
    column-gap: 12px;
    padding: 8px 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
}
.fd-cal-label {
    font-size: 11px;
    font-weight: 500;
    color: #64748b;
}
.fd-cal-value {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
}

/* 5. 메타 */
.fd-meta {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 10px 20px 14px;
}
.fd-meta-remark {
    font-size: 11px;
    color: #475569;
    font-weight: 500;
    line-height: 1.5;
}
.fd-meta-dates {
    font-size: 10px;
    color: #94a3b8;
    font-weight: 400;
}

/* 닫기 버튼 */
.fd-close-btn {
    background: #52b788;
    border: none;
    color: #ffffff;
    padding: 7px 20px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 8px;
    cursor: pointer;
    transition:
        filter 0.12s,
        transform 0.12s;
}
.fd-close-btn:hover {
    filter: brightness(0.94);
    transform: translateY(-1px);
}
.fd-close-btn:active {
    transform: scale(0.98);
}

/* 배출계수 등록 모달: 본문 스크롤 영역 + 커스텀 스크롤바 (HTML 디자인) */
.modal-form-content.registration-form {
    max-height: 70vh;
    overflow-y: auto;
    padding: 6px;
}
.modal-form-content.registration-form::-webkit-scrollbar {
    width: 6px;
}
.modal-form-content.registration-form::-webkit-scrollbar-track {
    background: #f1f5f9;
}
.modal-form-content.registration-form::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
}
.modal-form-content.registration-form::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

/* 통합 컨테이너: 신규 등록 시 emerald 박스 (HTML .bg-emerald-50/20 .border .rounded-2xl) */
.registration-unified {
    margin-bottom: 12px;
}
/* Bootstrap .row 음수 margin 제거 — 좌우 스크롤 방지 (overflow hidden 없이) */
.registration-form .row {
    margin-left: 0;
    margin-right: 0;
}

/* 계수 설정 카드: 그림자·깊이로 구분 (테두리 최소화) */
.form-section-gas-card {
    background: #fff !important;
    border: none !important;
    border-radius: 10px;
    padding: 0 !important;
    margin-top: 12px;
    box-shadow:
        0 1px 2px rgba(0, 0, 0, 0.04),
        0 4px 12px rgba(5, 150, 105, 0.08),
        0 0 0 1px rgba(5, 150, 105, 0.06);
}
.gas-card-header {
    background: linear-gradient(135deg, #059669 0%, #0d9488 50%, #10b981 100%);
    padding: 9px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 10px 10px 0 0;
    box-shadow: 0 2px 8px rgba(5, 150, 105, 0.2);
}
.gas-card-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
}
.gas-unit-picker {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
}
.gas-unit-picker__label {
    font-size: 11px;
    font-weight: 600;
    color: rgba(236, 253, 245, 0.95);
    letter-spacing: -0.01em;
    white-space: nowrap;
}
.gas-unit-picker__select {
    height: 32px;
    min-width: 200px;
    font-size: 12px;
    padding: 6px 10px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.35) !important;
    background: rgba(255, 255, 255, 0.92) !important;
    color: #064e3b;
}
/* 인풋+suffix 전체를 하나의 요소처럼: 바깥 컨테이너에만 border/radius 적용 */
.factor-input-group {
    flex-wrap: nowrap;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
    background: #ffffff;
    transition: border-color 0.15s ease;
}
/* 프로젝트 전역의 .form-control { width:100% } 오버라이드로 suffix 줄바꿈 방지 */
.factor-input-group > .form-control {
    width: 1% !important;
    flex: 1 1 auto;
    min-width: 0;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    outline: none !important;
    background: transparent;
    height: 34px;
    font-size: 13px;
    padding: 0 10px;
}
.factor-input-group > .form-control:focus {
    outline: none !important;
    box-shadow: none !important;
}
.factor-input-group .input-group-append {
    flex: 0 0 auto;
    display: flex;
    align-items: stretch;
}
.factor-input-group .input-group-text {
    white-space: nowrap;
}
/* suffix: 구분선 없이 배경 차이로만 구분 */
.factor-input-group .input-group-text.factor-suffix {
    display: inline-flex;
    align-items: center;
    padding: 0 9px;
    height: 34px;
    font-size: 11px;
    font-weight: 500;
    color: #94a3b8;
    background: #f8fafc;
    border: none !important;
    border-left: 1px solid #f1f5f9 !important;
    border-radius: 0 !important;
    box-shadow: none;
}
.gas-card-title {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: #ecfdf5;
    letter-spacing: 0.08em;
    display: flex;
    align-items: center;
    gap: 8px;
}
.gas-card-title i {
    font-size: 13px;
    color: #a7f3d0;
}
.gas-card-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    font-size: 10px;
    font-weight: 500;
    color: #065f46;
    background: rgba(255, 255, 255, 0.9);
    border: none;
    border-radius: 4px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}
.gas-card-body {
    border-radius: 0 0 8px 8px;
    padding: 12px;
    background: linear-gradient(180deg, #f7fef9 0%, #f0fdf4 100%);
}
.gas-card-body .form-group {
    margin-bottom: 16px;
}
.gas-card-body .form-group:last-child {
    margin-bottom: 0;
}

/* 계수 등록/수정 폼 전체 — 통일된 인풋 스타일 (가스 인풋과 동일 톤) */
.registration-form .form-control {
    border: 1px solid #e2e8f0 !important;
    border-radius: 6px !important;
    box-shadow: none !important;
    font-size: 13px;
    background: #fff !important;
}
.registration-form .form-control:focus {
    outline: none !important;
    box-shadow: none !important;
    border-color: #e2e8f0 !important;
}
.registration-form select.form-control {
    height: 34px;
    padding-top: 0;
    padding-bottom: 0;
}
/* 가스 인풋 내부는 컨테이너에서 border 관리 — 개별 border 제거 유지 */
.factor-input-group > .form-control {
    border: none !important;
}

/* 열량계수 서브섹션: 테두리 대신 여백 + 은은한 그림자로 구분 */
.form-section-gas-card .calorific-section {
    margin-top: 12px;
    padding-top: 12px;
    position: relative;
    padding-left: 10px;
    padding-right: 10px;
}
.form-section-gas-card .calorific-section::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(5, 150, 105, 0.12) 20%,
        rgba(5, 150, 105, 0.18) 50%,
        rgba(5, 150, 105, 0.12) 80%,
        transparent 100%
    );
    pointer-events: none;
}
.form-section-gas-card .calorific-section .small.text-muted {
    margin-top: 2px;
}

/* 열량계수: 라벨 위, 그 아래 값+단위 가로 배치. 좌 순발열량 | 우 총발열량 */
.calorific-section .calorific-cols {
    display: flex;
    flex-wrap: nowrap;
    gap: 24px;
}
.calorific-section .calorific-col {
    flex: 1;
    min-width: 0;
}
.calorific-section .calorific-col .calorific-label {
    display: block;
    margin-bottom: 6px;
    font-size: 12px;
    font-weight: 500;
    color: #374151;
    letter-spacing: -0.01em;
}
.calorific-section .calorific-input-group {
    display: flex;
    flex-wrap: nowrap;
    gap: 6px;
}

.calorific-section .calorific-unit-select {
    width: 65%;
}
</style>
